import {
  type FacetFilterDefinition,
  type ResultCount,
  type TechFilterDefinition,
} from "~/GearboxSelectionAssistant/models";
import { type CharacteristicKeys } from "~/GearboxSelectionAssistant/components/CharacteristicsFilter/types";
import { type DimensionKeys } from "~/GearboxSelectionAssistant/components/DimensionsFilter/types";
import { useReducer } from "react";
import { type GSASearchResultsDTO } from "~/GearboxSelectionAssistant/dtos";
import { produce, original } from "immer";
import { extractAllData } from "~/GearboxSelectionAssistant/transformers";
import { getInitialDimensionValues } from "~/GearboxSelectionAssistant/components/DimensionsFilter/constants";
import {
  CHARACTERISTIC_CODE_LIST_EXCEPT_TYPE,
  CHARACTERISTIC_WITH_FLANGE,
  Characteristics,
  FLANGE_SPECIFIC_CHARACTERISTICS,
  HIGH_PRECISION_CHARACTERISTIC_VALUE,
  selectedValuesContainFlange,
  VALID_VALUE_PREFIXES_FOR_HIGH_PRECISION,
  VALID_VALUE_PREFIXES_FOR_STRAIN_WAVE,
} from "~/GearboxSelectionAssistant/components/CharacteristicsFilter/constants";
import { transformSrpProduct } from "~/Search/transformers/transformSrpProduct";
import { SortDirection, type SortOption } from "~/Search/models";
import { type SrpProductData } from "~/utils/models";
import {
  type AllScreenSizes,
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";

type MaybeNumber = number | null | undefined;
export interface GSAPageState {
  initialised: boolean;
  fetching: "searching" | "loadingMore" | false;
  error: boolean;

  results: SrpProductData[];

  sortOptions: SortOption[];
  selectedSort: string;
  selectedSortDirection?: SortDirection;
  dimensionDefinitions: Record<string, TechFilterDefinition>;
  dimensionValues: Record<string, [MaybeNumber, MaybeNumber]>;
  characteristicDefinitions: Record<string, FacetFilterDefinition>;
  characteristicValues: Record<string, string[]>;
  resultCount: Record<string, ResultCount>;

  /** 0 indexed */
  currentPage: number;

  pageSize: number | null;

  totalAmountOfPages: number;
  totalAmountOfResults: number;
}

const INITIAL_SORT = "relevance";

const initialState: GSAPageState = {
  initialised: false,
  fetching: false,
  error: false,
  results: [],
  dimensionDefinitions: {},
  characteristicDefinitions: {},
  dimensionValues: {},
  characteristicValues: {},
  resultCount: {},

  selectedSort: INITIAL_SORT,
  selectedSortDirection: undefined,
  sortOptions: [],

  currentPage: 0,
  pageSize: null,
  totalAmountOfPages: 0,
  totalAmountOfResults: 0,
};

export const useGearboxSALogic = () => {
  const reducerData = useReducer(REDUCER, initialState);

  const screenSize = useNumericScreenSize();
  const newPageSize = PAGE_SIZES[screenSize];
  if (reducerData[0].pageSize !== newPageSize) {
    reducerData[1](ACTIONS.updatePageSize(newPageSize));
  }

  return reducerData;
};

export const useGSAFilterPopupLogic = (data: Partial<GSAPageState>) => {
  return useReducer(REDUCER, { ...data, initialised: true, fetching: false } as GSAPageState);
};

function searchStart(reason: "searching" | "loadingMore") {
  return {
    type: "searchStart",
    payload: reason,
  } as const;
}

function searchSuccess(results: GSASearchResultsDTO) {
  return {
    type: "searchSuccess",
    payload: results,
  } as const;
}

function searchError() {
  return {
    type: "searchError",
  } as const;
}

function resetCharacteristics() {
  return {
    type: "resetCharacteristics",
  } as const;
}
function resetDimensions() {
  return {
    type: "resetDimensions",
  } as const;
}

function resetAll() {
  return {
    type: "resetAll",
  } as const;
}

function updateDimension(key: DimensionKeys, value: [number | null | undefined, number | null | undefined]) {
  return {
    type: "updateDimension",
    payload: {
      key,
      value,
    },
  } as const;
}

function updateCharacteristic(key: CharacteristicKeys, value: string[]) {
  return {
    type: "updateCharacteristic",
    payload: {
      key,
      value,
    },
  } as const;
}

function updateFromModal(data: GSAPageState) {
  return {
    type: "updateFromModal",
    payload: data,
  } as const;
}

function updateSort(selectedSort: string, selectedSortDirection?: SortDirection) {
  return {
    type: "updateSort",
    payload: {
      selectedSort,
      selectedSortDirection,
    },
  } as const;
}

function updatePageSize(pageSize: number) {
  return {
    type: "updatePageSize",
    payload: pageSize,
  } as const;
}

function loadMore() {
  return {
    type: "loadMore",
  } as const;
}

export const ACTIONS = {
  searchStart,
  searchSuccess,
  searchError,
  resetCharacteristics,
  resetDimensions,
  resetAll,
  updateCharacteristic,
  updateDimension,
  updateFromModal,
  updateSort,
  updatePageSize,
  loadMore,
};

type ActionType = ReturnType<(typeof ACTIONS)[keyof typeof ACTIONS]>;

const REDUCER = (state: GSAPageState, action: ActionType): GSAPageState => {
  return produce(state, (draft: GSAPageState) => {
    switch (action.type) {
      case "searchStart":
        draft.fetching = action.payload;
        draft.error = false;
        break;
      case "searchSuccess": {
        draft.fetching = false;
        draft.error = false;
        if (draft.currentPage !== 0) {
          // We are loading more results - only need to append the results, the rest of the data should not change
          draft.results = [...draft.results, ...action.payload.results.map(transformSrpProduct)];
          break;
        }
        // It's a new query, we need to update all the data

        draft.results = action.payload.results.map(transformSrpProduct);
        draft.totalAmountOfResults = action.payload.pagination.totalNumberOfResults;
        draft.totalAmountOfPages = action.payload.pagination.numberOfPages;

        const searchData = extractAllData(action.payload);
        draft.resultCount = searchData.resultCount;
        draft.selectedSort = searchData.selectedSortCode;
        draft.selectedSortDirection = searchData.selectedSortDirection;

        if (!state.initialised) {
          draft.initialised = true;
          draft.dimensionDefinitions = searchData.dimensionDefinitions;
          draft.characteristicDefinitions = searchData.characteristicDefinitions;
          draft.sortOptions = searchData.sortOptions;
          draft.characteristicValues = {};
          draft.dimensionValues = getInitialDimensionValues(searchData.dimensionDefinitions);
        } else {
          // Update the selected values from backend
          // SCHAEFF-25860 - Preserve selected values in case the facet is not sent from BE at all...
          // Fixes issues when there are 0 results in the response.
          Object.entries(searchData.characteristicDefinitions).forEach(([key, value]) => {
            const selectedValuesFromFE = draft.characteristicValues[key];

            if (!selectedValuesFromFE) {
              draft.characteristicValues[key] = searchData.characteristicValues[key];
              return;
            }

            const originalSelectedValues = original(selectedValuesFromFE);
            const selectedValuesFromBE = searchData.characteristicValues[key];

            const availableValues = value.values;

            // Remove those values that are available and not selected in BE
            const nextValues = selectedValuesFromFE.filter((selectedValue) => {
              return !availableValues.find(
                (item) => item.code === selectedValue && !selectedValuesFromBE.includes(selectedValue),
              );
            });

            // Add those values that are selected in BE and missing from originalSelectedValues
            selectedValuesFromBE.forEach((selectedValue) => {
              if (!originalSelectedValues!.includes(selectedValue)) {
                nextValues.push(selectedValue);
              }
            });

            draft.characteristicValues[key] = nextValues;
          });
        }
        break;
      }
      case "searchError":
        draft.fetching = false;
        draft.error = true;
        break;
      case "resetCharacteristics":
        draft.characteristicValues = {};
        draft.currentPage = 0;
        break;
      case "resetDimensions":
        draft.dimensionValues = getInitialDimensionValues(draft.dimensionDefinitions);
        draft.currentPage = 0;
        break;
      case "resetAll":
        draft.characteristicValues = {};
        draft.dimensionValues = getInitialDimensionValues(draft.dimensionDefinitions);
        draft.currentPage = 0;
        break;
      case "updateDimension":
        draft.dimensionValues[action.payload.key] = action.payload.value;
        draft.currentPage = 0;
        break;
      case "updateCharacteristic":
        draft.characteristicValues[action.payload.key] = action.payload.value;
        draft.currentPage = 0;

        // SCHAEFF-28319 - We need to deselect incompatible values based on the product type...
        if (action.payload.key === Characteristics.PRODUCT_TYPE) {
          if (!action.payload.value.length) {
            // Clear all other characteristics if the product type is deselected
            CHARACTERISTIC_CODE_LIST_EXCEPT_TYPE.forEach((key) => {
              draft.characteristicValues[key] = [];
            });
          } else {
            // Validate the values for the high precision and strain wave characteristics
            const allowedValues = action.payload.value.includes(HIGH_PRECISION_CHARACTERISTIC_VALUE)
              ? VALID_VALUE_PREFIXES_FOR_HIGH_PRECISION
              : VALID_VALUE_PREFIXES_FOR_STRAIN_WAVE;
            Object.entries(allowedValues).forEach(([key, validValuePrefixes]) => {
              if (key in draft.characteristicValues) {
                draft.characteristicValues[key] = draft.characteristicValues[key].filter((value) =>
                  validValuePrefixes.some((prefix) => value.startsWith(prefix)),
                );
              }
            });
          }
        }

        // Remove values from the flange specific characteristics if the flange characteristic was deselected
        if (action.payload.key === CHARACTERISTIC_WITH_FLANGE && !selectedValuesContainFlange(action.payload.value)) {
          FLANGE_SPECIFIC_CHARACTERISTICS.forEach((key) => {
            draft.characteristicValues[key] = [];
          });
        }
        break;
      case "updateSort": {
        draft.currentPage = 0;

        const newCode = action.payload.selectedSort;
        if (draft.selectedSort === newCode) {
          // We're just changing the sort order
          draft.selectedSortDirection = action.payload.selectedSortDirection;
        } else {
          draft.selectedSort = newCode;
          // We are assigning a new code. We need to find out if the code can be asc/dec or not
          const isAscDesc = draft.sortOptions.find((option) => option.code === newCode)?.isAscdesc ?? false;
          if (isAscDesc) {
            // Assign asc by default for a new code
            draft.selectedSortDirection = SortDirection.ASC;
          } else {
            // Clear the direction since there's no asc/desc
            draft.selectedSortDirection = undefined;
          }
        }
        break;
      }
      case "updateFromModal":
        draft.currentPage = 0;
        draft.characteristicValues = action.payload.characteristicValues;
        draft.dimensionValues = action.payload.dimensionValues;
        // No need to process the flange case here - it was already processed in the modal
        break;
      case "loadMore":
        draft.currentPage++;
        break;
      case "updatePageSize":
        draft.currentPage = 0;
        draft.pageSize = action.payload;
        break;
    }
  });
};

const PAGE_SIZES: Record<AllScreenSizes, number> = {
  [SIZE_BELOW_PHONE]: 21,
  [SIZE_PHONE]: 21,
  [SIZE_TABLET]: 20,
  [SIZE_DESKTOP_S]: 21,
  [SIZE_DESKTOP_M]: 21,
  [SIZE_DESKTOP_L]: 21,
};
